import { useEffect, useRef, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import type { Room } from "./api";
import { ProjectPreview } from "./ProjectPreview";

export function ProjectCard({
  room,
  busy,
  open,
  archive,
  remove,
}: {
  room: Room;
  busy: boolean;
  open: () => void;
  archive: () => void;
  remove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!expanded) return;
    const close = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node)) setExpanded(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [expanded]);
  return (
    <article className="project-card-shell">
      <button
        className="project-card"
        onClick={open}
        disabled={busy}
        aria-label={`Open ${room.name}`}
      >
        <ProjectPreview room={room} />
        <div>
          <h3>
            {room.name}
            <ArrowUpRight size={18} />
          </h3>
          <p>
            {room.role} ·{" "}
            {new Date(room.created).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            })}
          </p>
        </div>
      </button>
      {room.role === "owner" && (
        <div
          className="project-actions"
          ref={menu}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
              setExpanded(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setExpanded(false);
              trigger.current?.focus();
            }
          }}
        >
          <button
            ref={trigger}
            className="project-actions-trigger"
            aria-label={`Manage ${room.name}`}
            aria-expanded={expanded}
            disabled={busy}
            onClick={() => setExpanded(!expanded)}
          >
            <MoreHorizontal size={20} />
          </button>
          {expanded && (
            <div className="project-actions-popover">
              <button
                onClick={() => {
                  setExpanded(false);
                  archive();
                }}
                disabled={busy}
              >
                {room.archivedAt ? (
                  <ArchiveRestore size={16} />
                ) : (
                  <Archive size={16} />
                )}
                {room.archivedAt ? "Restore project" : "Archive project"}
              </button>
              <button
                className="project-delete-action"
                onClick={() => {
                  setExpanded(false);
                  remove();
                }}
                disabled={busy}
              >
                <Trash2 size={16} />
                Delete project…
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
